'use client';

// The phone app's connectivity bar (BL-2342, mockup screens 5, 5b and 11): one
// bar pinned above every screen while the app is offline — blue "Offline mode"
// when the operator chose it, amber "No connection" when there is none. It
// replaces the black ConnectionHealthBar inside the app; desktop keeps that one.
// It carries one "Waiting to sync" indicator while any change is queued (BL-2685, was the
// "N not synced" count of BL-2438), read from the bridge because the bar sits above the signed-in
// shell that reads the queue, and opens Storage & Sync, whose body is that queue (BL-2566).

import Link from 'next/link';
import { ChevronRight, CloudOff, WifiOff } from 'lucide-react';
import clsx from 'clsx';
import { offlinePageHref } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-routes';
import { STORAGE_AND_SYNC_TITLE, WAITING_TO_SYNC } from './offline-copy';
import { useOfflineMode } from './OfflineModeProvider';
import { useChangeQueuePending } from './ChangeQueueCountBridge';

/**
 * Render the bar from the app's offline mode. Takes no props: the mode comes
 * from `OfflineModeProvider` (ADR 029 — View Units).
 *
 * In-flow like the black bar, so it pushes the page down rather than covering
 * it, and renders nothing while online.
 *
 * @returns the bar, or null while online.
 */
export default function NativeConnectivityBar() {
  const { mode } = useOfflineMode();
  const waiting = useChangeQueuePending() === true;
  if (mode === 'online') return null;

  const manual = mode === 'manual';
  const Icon = manual ? CloudOff : WifiOff;

  const label = manual ? 'Offline mode' : 'No connection';
  return (
    // The wrapper announces the state (role=status is polite by default); the
    // link inside stays a link, so assistive tech reads it as a way to
    // Storage & Sync rather than as a status region.
    <div role="status" data-testid="native-connectivity-bar" data-mode={mode} className="w-full shrink-0">
      <Link
        href={offlinePageHref()}
        // Never prefetched: signed out (the sign-in page shows this bar), the server answers a
        // prefetch of /offline with a redirect to sign-in, Next caches it, and the post-sign-in
        // navigation to /offline lands on that cached redirect, stranding the form (BUG-2197).
        prefetch={false}
        aria-label={waiting ? `${label}, ${WAITING_TO_SYNC.toLowerCase()}, open ${STORAGE_AND_SYNC_TITLE}` : `${label}, open ${STORAGE_AND_SYNC_TITLE}`}
        className={clsx(
          'flex h-9 w-full items-center gap-density-cluster px-density-row-x',
          'text-caption font-semibold text-white',
          manual ? 'bg-connectivity-manual' : 'bg-connectivity-no-connection',
        )}
      >
        <Icon className="h-4 w-4 flex-shrink-0" aria-hidden />
        <span className="flex-1">{label}</span>
        {waiting && (
          <span className="font-medium" data-testid="native-connectivity-bar-waiting">
            {WAITING_TO_SYNC}
          </span>
        )}
        <ChevronRight className="h-4 w-4 flex-shrink-0" aria-hidden />
      </Link>
    </div>
  );
}
